/**
 * Enterprise-grade Data Fetching Hooks
 *
 * Features for billion-scale:
 * - Cursor-based pagination (scales better than offset)
 * - Optimistic updates
 * - Request deduplication
 * - Automatic retry
 * - Stale-while-revalidate pattern
 * - Infinite scroll support
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { requestManager } from '../lib/RequestManager';

/**
 * Core data fetching hook with caching and deduplication
 */
export function useQuery(fetchFn, options = {}) {
  const {
    key,
    enabled = true,
    cacheTTL = 30000,
    staleTime = 5000,
    refetchOnMount = true,
    refetchInterval = null,
    onSuccess,
    onError,
  } = options;

  const [state, setState] = useState({
    data: null,
    error: null,
    isLoading: enabled,
    isFetching: false,
    isStale: false,
  });

  const mountedRef = useRef(true);
  const lastFetchRef = useRef(0);

  const execute = useCallback(async (skipCache = false) => {
    if (!enabled) return;

    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchRef.current;

    // Skip if data is fresh
    if (!skipCache && timeSinceLastFetch < staleTime && state.data) {
      return state.data;
    }

    setState(prev => ({
      ...prev,
      isFetching: true,
      isStale: timeSinceLastFetch > staleTime,
    }));

    try {
      const result = await requestManager.execute(
        () => fetchFn(),
        {
          cacheKey: key,
          cacheTTL,
          skipCache,
          abortKey: key,
        }
      );

      if (mountedRef.current) {
        lastFetchRef.current = Date.now();
        setState({
          data: result,
          error: null,
          isLoading: false,
          isFetching: false,
          isStale: false,
        });
        onSuccess?.(result);
      }

      return result;
    } catch (error) {
      if (error.name === 'AbortError') return;

      if (mountedRef.current) {
        setState(prev => ({
          ...prev,
          error: error.message || 'An error occurred',
          isLoading: false,
          isFetching: false,
        }));
        onError?.(error);
      }

      throw error;
    }
  }, [fetchFn, key, enabled, cacheTTL, staleTime, onSuccess, onError]);

  // Initial fetch
  useEffect(() => {
    if (enabled && refetchOnMount) {
      execute();
    }

    return () => {
      mountedRef.current = false;
      if (key) {
        requestManager.abort(key);
      }
    };
  }, [key, enabled]);

  // Polling
  useEffect(() => {
    if (!refetchInterval || !enabled) return;

    const interval = setInterval(() => {
      execute(true);
    }, refetchInterval);

    return () => clearInterval(interval);
  }, [refetchInterval, enabled, execute]);

  const refetch = useCallback(() => execute(true), [execute]);

  const invalidate = useCallback(() => {
    if (key) {
      requestManager.invalidateCache(key);
    }
    return execute(true);
  }, [key, execute]);

  return {
    ...state,
    refetch,
    invalidate,
  };
}

/**
 * Mutation hook with optimistic updates
 */
export function useMutation(mutationFn, options = {}) {
  const {
    onSuccess,
    onError,
    onMutate, // For optimistic updates
    invalidateKeys = [],
  } = options;

  const [state, setState] = useState({
    data: null,
    error: null,
    isLoading: false,
  });

  const mutate = useCallback(async (variables) => {
    setState({ data: null, error: null, isLoading: true });

    // Optimistic update callback
    const rollback = onMutate?.(variables);

    try {
      const result = await mutationFn(variables);

      setState({ data: result, error: null, isLoading: false });

      // Invalidate related caches
      invalidateKeys.forEach(key => {
        requestManager.invalidateCache(key);
      });

      onSuccess?.(result, variables);
      return result;
    } catch (error) {
      // Rollback optimistic update
      rollback?.();

      setState({ data: null, error: error.message, isLoading: false });
      onError?.(error, variables);
      throw error;
    }
  }, [mutationFn, onSuccess, onError, onMutate, invalidateKeys]);

  const reset = useCallback(() => {
    setState({ data: null, error: null, isLoading: false });
  }, []);

  return {
    ...state,
    mutate,
    mutateAsync: mutate,
    reset,
  };
}

/**
 * Cursor-based pagination hook for infinite scroll
 * Scales to billions of records without performance degradation
 */
export function useInfiniteQuery(fetchFn, options = {}) {
  const {
    key,
    enabled = true,
    getNextPageParam, // (lastPage) => nextCursor | undefined
    initialCursor = null,
  } = options;

  const [state, setState] = useState({
    pages: [],
    error: null,
    isLoading: enabled,
    isFetchingNextPage: false,
    hasNextPage: true,
  });

  const cursorRef = useRef(initialCursor);
  const mountedRef = useRef(true);

  const fetchPage = useCallback(async (cursor, isInitial = false) => {
    if (!enabled) return;

    setState(prev => ({
      ...prev,
      isLoading: isInitial,
      isFetchingNextPage: !isInitial,
    }));

    try {
      const result = await requestManager.execute(
        () => fetchFn(cursor),
        {
          cacheKey: `${key}:${cursor || 'initial'}`,
          cacheTTL: 30000,
        }
      );

      if (!mountedRef.current) return;

      const nextCursor = getNextPageParam?.(result);
      cursorRef.current = nextCursor;

      setState(prev => ({
        pages: isInitial ? [result] : [...prev.pages, result],
        error: null,
        isLoading: false,
        isFetchingNextPage: false,
        hasNextPage: nextCursor !== undefined && nextCursor !== null,
      }));

      return result;
    } catch (error) {
      if (error.name === 'AbortError') return;

      if (mountedRef.current) {
        setState(prev => ({
          ...prev,
          error: error.message,
          isLoading: false,
          isFetchingNextPage: false,
        }));
      }
    }
  }, [fetchFn, key, enabled, getNextPageParam]);

  // Initial fetch
  useEffect(() => {
    if (enabled) {
      fetchPage(initialCursor, true);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [key, enabled]);

  const fetchNextPage = useCallback(() => {
    if (state.hasNextPage && !state.isFetchingNextPage) {
      return fetchPage(cursorRef.current);
    }
  }, [fetchPage, state.hasNextPage, state.isFetchingNextPage]);

  const refetch = useCallback(() => {
    cursorRef.current = initialCursor;
    return fetchPage(initialCursor, true);
  }, [fetchPage, initialCursor]);

  // Flatten all pages data
  const data = useMemo(() => {
    return state.pages.flatMap(page =>
      page.data || page.items || page.users || page.results || []
    );
  }, [state.pages]);

  return {
    data,
    pages: state.pages,
    error: state.error,
    isLoading: state.isLoading,
    isFetchingNextPage: state.isFetchingNextPage,
    hasNextPage: state.hasNextPage,
    fetchNextPage,
    refetch,
  };
}

/**
 * Debounced value hook for search inputs
 */
export function useDebouncedValue(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Intersection observer hook for infinite scroll trigger
 */
export function useIntersectionObserver(options = {}) {
  const { threshold = 0.1, rootMargin = '100px' } = options;
  const [entry, setEntry] = useState(null);
  const [node, setNode] = useState(null);

  const observer = useRef(null);

  useEffect(() => {
    if (observer.current) {
      observer.current.disconnect();
    }

    if (!node) return;

    observer.current = new IntersectionObserver(
      ([entry]) => setEntry(entry),
      { threshold, rootMargin }
    );

    observer.current.observe(node);

    return () => {
      observer.current?.disconnect();
    };
  }, [node, threshold, rootMargin]);

  return [setNode, entry?.isIntersecting ?? false];
}

export default {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useDebouncedValue,
  useIntersectionObserver,
};
